import {Farm} from "../../../types/domain/farm";
import {useAtom, useAtomValue} from "jotai";
import {activeMergedFarmIds} from "../../../store/activeMergedFarmIds";
import {useCallback, useEffect, useState} from "react";
import {Collapse, IconButton, List, Stack, Typography} from "@mui/material";
import {Field} from "../../../types/domain/field";
import {StatusIndicator} from "../../base/StatusIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {useTranslation} from "react-i18next";
import {FieldStatus} from "../../../types/domain/fieldStatus";
import {getAllFields} from "../../../services/fieldService";
import {useNavigate} from "react-router-dom";
import {isCropGrowthAlert, isSoilMoistureAlert} from "../../../utils/insightsUtils";
import {activeCompanyResultsLoadable} from "../../../store/activeCompanyResultsStore";
import {isMaderaCompany} from "../../../utils/maderaUtils";
import {Company} from "../../../types/domain/company";

interface FarmListItemProps {
    farm: Farm;
    active: boolean;
    cropAlert: boolean;
    moistureAlert: boolean;
    companyId: string;
    fieldNameFilter: string;
    activeFieldId: string | undefined;
    foundFields: Field[];
    isSearching: boolean;
    fieldCropFilter: string | undefined;
    activeFieldsFilter: boolean;
    basePath: "insights" | "map";
    setActiveYear?: (val: number | undefined) => void;
    parentCompany: Company | undefined;
}

export const FarmListItem: React.FC<FarmListItemProps> = ({
    farm,
    active,
    cropAlert,
    moistureAlert,
    companyId,
    fieldNameFilter,
    activeFieldId,
    foundFields,
    isSearching,
    fieldCropFilter,
    activeFieldsFilter,
    basePath,
    setActiveYear,
    parentCompany,
}) => {
    const [, setActiveMergedFarmIdsAtom] = useAtom(activeMergedFarmIds);
    const {t} = useTranslation();
    const [fields, setFields] = useState<Field[]>([]);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const activeCompanyResultsState = useAtomValue(activeCompanyResultsLoadable);

    const loadFields = useCallback(async () => {
        const response = await getAllFields({
            companyId,
            farmId: farm.id,
            cropId: fieldCropFilter,
            onlyActiveOrders: activeFieldsFilter,
            attributes: ["id", "name", "farmId", "companyId", "status", "irrigationCriteria"],
        });

        setFields(response);
    }, [companyId, farm.id, fieldCropFilter, activeFieldsFilter, setFields]);

    useEffect(() => {
        if (open) {
            void loadFields();
        } else {
            setFields([]);
        }
    }, [loadFields, open, parentCompany]);

    function activateFarm() {
        if (setActiveYear) {
            setActiveYear(undefined);
        }
        setActiveMergedFarmIdsAtom([farm.id]);
        navigate({
            pathname: `/${basePath}/${companyId}/farm/${farm.id}`,
            search: location.search,
        });
    }

    function activateField(field: Field) {
        if (setActiveYear) {
            setActiveYear(undefined);
        }
        navigate({
            pathname: `/${basePath}/${field.companyId}/field/${field.id}`,
            search: location.search,
        });
    }

    useEffect(() => {
        setOpen(!!(isSearching && foundFields?.length));
    }, [foundFields, isSearching]);

    const moistureAlertThreshold = (id: string) => fields.find((item) => item.id === id)?.irrigationCriteria ?? 0.1;

    const highlightMatch = (text: string, query: string | null) => {
        if (!query) return text;

        const regex = new RegExp(`(${query})`, "gi");
        const parts = text.split(regex);

        return (
            <>
                {parts.map((part, index) =>
                    part.toLowerCase() === query.toLowerCase() ? (
                        <span key={index} style={{backgroundColor: "rgb(254, 217, 118)", fontWeight: "bold"}}>
                            {part}
                        </span>
                    ) : (
                        part
                    ),
                )}
            </>
        );
    };

    return (
        <Stack>
            <div
                className="farmListItem"
                style={{
                    cursor: "pointer",
                    borderBottom: "1px solid #E0E0E0",
                    backgroundColor: active ? "#5161C2" : "",
                    color: active ? "white" : "",
                    paddingLeft: parentCompany && isMaderaCompany(parentCompany) ? "30px" : "0px",
                }}
                onClick={() => activateFarm()}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap="1rem" width="100%">
                    <Stack direction="row" alignItems="center" minWidth="0">
                        <IconButton
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setOpen(!open);
                            }}
                        >
                            {open ? (
                                <ExpandMoreIcon
                                    sx={{
                                        color: active ? "#DBDEE6" : "#6476CB",
                                        width: "1.4rem",
                                        height: "1.4rem",
                                    }}
                                />
                            ) : (
                                <ArrowRightIcon
                                    sx={{
                                        color: active ? "#DBDEE6" : "#6476CB",
                                        width: "1.4rem",
                                        height: "1.4rem",
                                    }}
                                />
                            )}
                        </IconButton>
                        <Typography textTransform="uppercase" textOverflow="ellipsis" noWrap>
                            {farm.name}
                        </Typography>
                    </Stack>

                    <Stack direction="row" alignItems="center" gap="0.5rem" marginRight={1}>
                        <StatusIndicator status={moistureAlert ? "moistureAlert" : "normal"} />
                        <StatusIndicator status={cropAlert ? "cropAlert" : "normal"} />
                    </Stack>
                </Stack>
            </div>
            <Collapse in={open} unmountOnExit>
                <List
                    component="div"
                    disablePadding
                    sx={{
                        display: "flex",
                        flex: 1,
                        overflowX: "hidden",
                        flexDirection: "column",
                    }}
                >
                    {(isSearching ? foundFields : fields)?.map((field, index) => (
                        <div
                            onClick={() => activateField(field)}
                            style={{
                                padding: "9px",
                                display: "flex",
                                flexDirection: "row",
                                flex: 1,
                                justifyContent: "space-between",
                                cursor: "pointer",
                                backgroundColor: activeFieldId === field.id ? "#5161C2" : "",
                                color: activeFieldId === field.id ? "white" : "",
                                paddingLeft: parentCompany && isMaderaCompany(parentCompany) ? "35px" : "0px",
                            }}
                            className="fieldListItem"
                            key={index}
                        >
                            <div
                                style={{
                                    flex: 1,
                                    justifyContent: "space-between",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    marginLeft: "35px",
                                    marginRight: "15px",
                                }}
                            >
                                {highlightMatch(field.name, fieldNameFilter)}
                            </div>

                            {field.status === FieldStatus.FINAL && (
                                <>
                                    <div
                                        style={{
                                            height: "15px",
                                            width: "15px",
                                            marginRight: "8px",
                                            borderRadius: "25px",
                                            backgroundColor:
                                                activeCompanyResultsState.state === "hasData" &&
                                                isSoilMoistureAlert(
                                                    activeCompanyResultsState.data[field.id]?.soilMoisture,
                                                    moistureAlertThreshold(field.id),
                                                    activeCompanyResultsState.data[field.id]?.vegetationCover,
                                                )
                                                    ? "#F67A6D"
                                                    : "#F3F5FA",
                                        }}
                                    ></div>
                                    <div
                                        style={{
                                            height: "15px",
                                            width: "15px",
                                            borderRadius: "25px",
                                            backgroundColor:
                                                activeCompanyResultsState.state === "hasData" &&
                                                isCropGrowthAlert(
                                                    activeCompanyResultsState.data[field.id]?.cropGrowth,
                                                    activeCompanyResultsState.data[field.id]?.vegetationCover,
                                                )
                                                    ? "#FFEEA0"
                                                    : "#F3F5FA",
                                        }}
                                    ></div>
                                </>
                            )}

                            {field.status === FieldStatus.DRAFT && (
                                <div
                                    style={{
                                        marginRight: "1rem",
                                        textTransform: "uppercase",
                                        fontSize: "12px",
                                        color: "#8F94A8",
                                    }}
                                >
                                    {t("base_filter_panel.draft_field_badge")}
                                </div>
                            )}
                        </div>
                    ))}
                </List>
            </Collapse>
        </Stack>
    );
};
